import { useState, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import type { LoopMetric, MetricsDayPoint, MetricsDto, ProjectMetric, RunStatus, TokenUsageDto } from '@aiai/shared';
import { fetchMetrics, type MetricsRange } from '../api';
import { qk } from '../api/queryKeys';
import { errorMessage } from '../lib/api';
import { RUN_STATUS_TONE } from '../lib/statusTones';
import { Badge } from '../components/Badge';
import { PageHeader } from '../components/PageHeader';
import { SectionCard } from '../components/SectionCard';
import { SegmentedTabs } from '../components/Tabs';
import { EmptyState, ErrorState, LoadingState } from '../components/States';
import { formatCost, formatTokens, titleCase } from '../lib/format';
import { projectHref } from '../lib/projectNav';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { useWorkspacePath } from '../hooks/useWorkspacePath';
import { useOrg } from '../org/useOrg';
import { WorkspaceSwitcher } from '../components/WorkspaceSwitcher';

type PresetValue = '1' | '7' | '30' | '90' | '365' | 'all';
type WindowValue = PresetValue | 'custom';

const WINDOW_OPTIONS: Array<{ value: WindowValue; label: string }> = [
  { value: '1', label: '24h' },
  { value: '7', label: '7d' },
  { value: '30', label: '30d' },
  { value: '90', label: '90d' },
  { value: '365', label: '1y' },
  { value: 'all', label: 'All' },
  { value: 'custom', label: 'Custom' },
];

const DEFAULT_PRESET: PresetValue = '30';
const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

/**
 * The selected range lives in the URL (`?range=7|all|…` or `?from=&to=`) so it
 * survives reloads and can be shared. Anything unrecognised falls back to 30d.
 */
function rangeFromParams(params: URLSearchParams): { tab: WindowValue; range: MetricsRange } {
  const from = params.get('from') ?? '';
  const to = params.get('to') ?? '';
  if (DATE_RE.test(from) && DATE_RE.test(to) && from <= to) {
    return { tab: 'custom', range: { from, to } };
  }
  const preset = (WINDOW_OPTIONS.find((o) => o.value === params.get('range') && o.value !== 'custom')
    ?.value ?? DEFAULT_PRESET) as PresetValue;
  return preset === 'all'
    ? { tab: 'all', range: { all: true } }
    : { tab: preset, range: { days: Number(preset) } };
}

function rangeDescription(range: MetricsRange): string {
  if ('all' in range) return 'across all time';
  if ('from' in range) return `from ${range.from} to ${range.to}`;
  return range.days === 1 ? 'over the last 24 hours' : `over the last ${range.days} days`;
}

/** YYYY-MM-DD (UTC — the API buckets by UTC day) `daysAgo` days before today. */
function utcDate(daysAgo = 0): string {
  return new Date(Date.now() - daysAgo * 24 * 60 * 60 * 1000).toISOString().slice(0, 10);
}

/** From/to date inputs for the Custom range; Apply is disabled until valid. */
function CustomRangeForm({
  initial,
  onApply,
}: {
  initial: { from: string; to: string };
  onApply: (from: string, to: string) => void;
}) {
  const [from, setFrom] = useState(initial.from);
  const [to, setTo] = useState(initial.to);
  const valid = DATE_RE.test(from) && DATE_RE.test(to) && from <= to;
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (valid) onApply(from, to);
  };
  return (
    <form onSubmit={submit} className="flex flex-wrap items-center justify-end gap-2">
      <input
        type="date"
        aria-label="From"
        className="input"
        value={from}
        max={to || undefined}
        onChange={(e) => setFrom(e.target.value)}
      />
      <span className="text-xs text-ink-faint">to</span>
      <input
        type="date"
        aria-label="To"
        className="input"
        value={to}
        min={from || undefined}
        onChange={(e) => setTo(e.target.value)}
      />
      <button type="submit" className="btn-primary btn-sm" disabled={!valid}>
        Apply
      </button>
    </form>
  );
}

function StatCard({
  label,
  value,
  sub,
}: {
  label: string;
  value: string;
  sub?: string;
}) {
  return (
    <div className="card p-4">
      <div className="font-display text-2xs font-semibold uppercase tracking-wide text-ink-muted">
        {label}
      </div>
      <div className="mt-1 font-display text-2xl font-bold text-ink tabular-nums">{value}</div>
      {sub && <div className="mt-0.5 text-xs text-ink-faint">{sub}</div>}
    </div>
  );
}

function fmtDuration(seconds: number | null): string {
  if (seconds == null) return '—';
  if (seconds < 60) return `${seconds}s`;
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  if (m < 60) return `${m}m ${s}s`;
  const h = Math.floor(m / 60);
  return `${h}h ${m % 60}m`;
}

type Bucket = MetricsDto['seriesBucket'];

/** Tooltip/axis label for a series point: weekly points read "Week of …". */
function pointLabel(date: string, bucket: Bucket): string {
  return bucket === 'week' ? `Week of ${date}` : date;
}

/** Stacked bars per day/week: succeeded (green) at the base, other runs above. */
function RunsChart({ series, bucket }: { series: MetricsDayPoint[]; bucket: Bucket }) {
  const max = Math.max(1, ...series.map((p) => p.runs));
  return (
    <div className="card p-4">
      <div className="mb-3 flex items-center justify-between">
        <h2 className="text-sm font-semibold text-ink">Runs per {bucket}</h2>
        <div className="flex items-center gap-3 text-2xs text-ink-faint">
          <span className="flex items-center gap-1">
            <span className="inline-block h-2 w-2 rounded-sm bg-ok" />
            succeeded
          </span>
          <span className="flex items-center gap-1">
            <span className="inline-block h-2 w-2 rounded-sm bg-surface-4" />
            other
          </span>
        </div>
      </div>
      <div className="flex h-40 items-end gap-px">
        {series.map((p) => {
          const others = Math.max(0, p.runs - p.succeeded);
          return (
            <div
              key={p.date}
              className="flex h-full flex-1 flex-col justify-end"
              title={`${pointLabel(p.date, bucket)}: ${p.runs} run${p.runs === 1 ? '' : 's'}, ${p.succeeded} succeeded, ${formatCost(p.costUsd)}`}
            >
              {others > 0 && (
                <div
                  className="w-full rounded-t-sm bg-surface-4"
                  style={{ height: `${(others / max) * 100}%` }}
                />
              )}
              <div
                className="w-full bg-ok"
                style={{ height: `${(p.succeeded / max) * 100}%` }}
              />
            </div>
          );
        })}
      </div>
      <AxisLabels series={series} bucket={bucket} />
    </div>
  );
}

function CostChart({ series, bucket }: { series: MetricsDayPoint[]; bucket: Bucket }) {
  const max = Math.max(0.0001, ...series.map((p) => p.costUsd));
  const total = series.reduce((a, p) => a + p.costUsd, 0);
  return (
    <div className="card p-4">
      <div className="mb-3 flex items-center justify-between">
        <h2 className="text-sm font-semibold text-ink">Cost per {bucket}</h2>
        <span className="font-mono text-xs text-ink-muted">
          {formatCost(total)} total
        </span>
      </div>
      <div className="flex h-40 items-end gap-px">
        {series.map((p) => (
          <div
            key={p.date}
            className="flex h-full flex-1 flex-col justify-end"
            title={`${pointLabel(p.date, bucket)}: ${formatCost(p.costUsd)}`}
          >
            <div
              className="w-full rounded-t-sm bg-ok"
              style={{ height: `${(p.costUsd / max) * 100}%` }}
            />
          </div>
        ))}
      </div>
      <AxisLabels series={series} bucket={bucket} />
    </div>
  );
}

function AxisLabels({ series, bucket }: { series: MetricsDayPoint[]; bucket: Bucket }) {
  if (!series.length) return null;
  const firstDate = series[0].date;
  const lastDate = series[series.length - 1].date;
  // Drop the year only when the whole range sits inside one year.
  const short = (d: string) => (firstDate.slice(0, 4) === lastDate.slice(0, 4) ? d.slice(5) : d);
  const first = pointLabel(short(firstDate), bucket);
  const last = pointLabel(short(lastDate), bucket);
  return (
    <div className="mt-1.5 flex justify-between font-mono text-2xs text-ink-faint">
      <span>{first}</span>
      <span>{last}</span>
    </div>
  );
}

function RoleStat({ label, value }: { label: string; value: string }) {
  return (
    <span className="text-xs text-ink">
      <span className="text-ink-faint">{label} </span>
      <span className="font-mono tabular-nums">{value}</span>
    </span>
  );
}

function RoleRow({ role }: { role: MetricsDto['roles'][number] }) {
  return (
    <li className="flex flex-wrap items-center gap-x-4 gap-y-1 px-4 py-2.5">
      <span className="min-w-0 flex-1 basis-32 text-sm text-ink">
        {titleCase(role.role)}
      </span>
      <RoleStat label="Runs" value={String(role.count)} />
      <RoleStat label="Avg time" value={fmtDuration(role.avgDurationSeconds)} />
      <RoleStat label="Avg cost" value={formatCost(role.avgCostUsd)} />
    </li>
  );
}

function RolesTable({ roles }: { roles: MetricsDto['roles'] }) {
  return (
    <SectionCard title="By role" count={roles.length}>
      {roles.length === 0 ? (
        <EmptyState compact title="No stage data in this window." />
      ) : (
        <ul className="divide-y divide-line">
          {roles.map((r) => (
            <RoleRow key={r.role} role={r} />
          ))}
        </ul>
      )}
    </SectionCard>
  );
}

function LoopChurnSection({
  avgLoopIterations,
  totalGateFailures,
  gateFailureRate,
}: {
  avgLoopIterations: number | null;
  totalGateFailures: number;
  gateFailureRate: number | null;
}) {
  if (avgLoopIterations == null) return null;
  const failPct = gateFailureRate == null ? '—' : `${Math.round(gateFailureRate * 100)}%`;
  return (
    <div className="card p-4">
      <h2 className="mb-3 text-sm font-semibold text-ink">Loop churn</h2>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <StatCard
          label="Avg iterations"
          value={avgLoopIterations.toFixed(2)}
          sub="1.0 = passed on first try"
        />
        <StatCard
          label="Gate failures"
          value={String(totalGateFailures)}
          sub="total failed gate checks"
        />
        <StatCard
          label="Runs with failures"
          value={failPct}
          sub="had ≥1 gate failure"
        />
      </div>
    </div>
  );
}

function FleetBenchmarkSection({
  data,
}: {
  data: Pick<MetricsDto, 'gateFailureRate' | 'mergedPrCount' | 'costPerMergedPr'>;
}) {
  const gatePassPct =
    data.gateFailureRate == null
      ? '—'
      : `${Math.round((1 - data.gateFailureRate) * 100)}%`;
  return (
    <div className="card p-4">
      <h2 className="mb-3 text-sm font-semibold text-ink">Fleet quality</h2>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <StatCard
          label="Gate pass-rate"
          value={gatePassPct}
          sub="runs that passed on first try"
        />
        <StatCard
          label="Merged PRs"
          value={String(data.mergedPrCount)}
          sub="PRs merged in this window"
        />
        <StatCard
          label="Cost / merged PR"
          value={formatCost(data.costPerMergedPr)}
          sub="all-in fleet efficiency"
        />
      </div>
    </div>
  );
}

function TokenUsageSection({ tokenUsage }: { tokenUsage: TokenUsageDto | null }) {
  if (tokenUsage == null) return null;
  const totalCache = tokenUsage.cacheCreationTokens + tokenUsage.cacheReadTokens;
  const total =
    tokenUsage.inputTokens + tokenUsage.outputTokens + totalCache;
  return (
    <div className="card p-4">
      <h2 className="mb-3 text-sm font-semibold text-ink">Token usage</h2>
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        <StatCard
          label="Input"
          value={formatTokens(tokenUsage.inputTokens)}
          sub="prompt tokens"
        />
        <StatCard
          label="Output"
          value={formatTokens(tokenUsage.outputTokens)}
          sub="generated tokens"
        />
        <StatCard
          label="Cache"
          value={formatTokens(totalCache)}
          sub={`${formatTokens(tokenUsage.cacheCreationTokens)} write · ${formatTokens(tokenUsage.cacheReadTokens)} read`}
        />
        <StatCard
          label="Total"
          value={formatTokens(total)}
          sub="all token types"
        />
      </div>
    </div>
  );
}

function ProjectRow({ project }: { project: ProjectMetric }) {
  const { wp } = useWorkspacePath();
  return (
    <li className="flex flex-wrap items-center gap-x-4 gap-y-1 px-4 py-2.5 transition-colors hover:bg-surface-2">
      {project.projectId === null ? (
        <span className="min-w-0 flex-1 basis-32 text-sm text-ink-faint">
          {project.projectName} <span className="text-xs">(deleted)</span>
        </span>
      ) : (
        <Link
          to={wp(projectHref(project.projectKind, project.projectCode ?? project.projectId))}
          className="min-w-0 flex-1 basis-32 text-sm text-ink hover:text-signal"
        >
          {project.projectName}
        </Link>
      )}
      <span className="text-xs text-ink">
        <span className="text-ink-faint">Runs </span>
        <span className="font-mono tabular-nums">{project.runCount}</span>
      </span>
      <span className="text-xs text-ink">
        <span className="text-ink-faint">Cost </span>
        <span className="font-mono tabular-nums">{formatCost(project.totalCostUsd)}</span>
      </span>
    </li>
  );
}

function ByProjectTable({ projects }: { projects: ProjectMetric[] | undefined }) {
  const list = projects ?? [];
  return (
    <SectionCard title="By project" count={list.length}>
      {list.length === 0 ? (
        <EmptyState compact title="No runs in this window." />
      ) : (
        <ul className="divide-y divide-line">
          {list.map((p) => (
            <ProjectRow key={p.projectId ?? `deleted:${p.projectKind}:${p.projectName}`} project={p} />
          ))}
        </ul>
      )}
    </SectionCard>
  );
}

function LoopRow({ loop }: { loop: LoopMetric }) {
  const { wp } = useWorkspacePath();
  return (
    <li className="flex flex-wrap items-center gap-x-4 gap-y-1 px-4 py-2.5 transition-colors hover:bg-surface-2">
      <Link
        to={wp(`/projects/${loop.projectCode ?? loop.projectId}/loops/${loop.loopId}/edit`)}
        className="min-w-0 flex-1 basis-32 text-sm text-ink hover:text-signal"
      >
        {loop.loopName}
      </Link>
      <span className="text-xs text-ink">
        <span className="text-ink-faint">Runs </span>
        <span className="font-mono tabular-nums">{loop.runCount}</span>
      </span>
      <span className="text-xs text-ink">
        <span className="text-ink-faint">Cost </span>
        <span className="font-mono tabular-nums">{formatCost(loop.totalCostUsd)}</span>
      </span>
    </li>
  );
}

function ByLoopTable({ loops }: { loops: LoopMetric[] | undefined }) {
  const list = loops ?? [];
  return (
    <SectionCard title="By loop" count={list.length}>
      {list.length === 0 ? (
        <EmptyState compact title="No loop runs in this window." />
      ) : (
        <ul className="divide-y divide-line">
          {list.map((l) => (
            <LoopRow key={l.loopId} loop={l} />
          ))}
        </ul>
      )}
    </SectionCard>
  );
}

function StatusBreakdown({ byStatus }: { byStatus: Record<string, number> }) {
  const entries = Object.entries(byStatus).sort((a, b) => b[1] - a[1]);
  if (!entries.length) return null;
  return (
    <div className="flex flex-wrap gap-2">
      {entries.map(([status, count]) => (
        <Badge
          key={status}
          tone={RUN_STATUS_TONE[status as RunStatus] ?? 'neutral'}
          size="md"
        >
          {titleCase(status)}{' '}
          <span className="font-mono">{count}</span>
        </Badge>
      ))}
    </div>
  );
}

export function MetricsPage() {
  useDocumentTitle('Metrics');
  const { activeOrg } = useOrg();
  const [searchParams, setSearchParams] = useSearchParams();
  const { tab, range } = rangeFromParams(searchParams);
  // Picking "Custom" only opens the date form; the range changes on Apply.
  const [customOpen, setCustomOpen] = useState(false);

  const updateParams = (set: Record<string, string>) =>
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        for (const k of ['range', 'from', 'to']) next.delete(k);
        for (const [k, v] of Object.entries(set)) next.set(k, v);
        return next;
      },
      { replace: true },
    );

  const onTabChange = (value: WindowValue) => {
    if (value === 'custom') {
      setCustomOpen(true);
      return;
    }
    setCustomOpen(false);
    updateParams(value === DEFAULT_PRESET ? {} : { range: value });
  };

  const onApplyCustom = (from: string, to: string) => {
    setCustomOpen(false);
    updateParams({ from, to });
  };

  // Scoped to the active workspace: the request carries X-Org-Id and the server
  // aggregates only that workspace's runs. Key by org id so switching workspaces
  // fetches fresh data instead of showing the previous one's cached numbers.
  const query = useQuery({
    queryKey: qk.metrics(range, activeOrg?.id),
    queryFn: () => fetchMetrics(range),
  });

  return (
    <div className="space-y-6">
      <PageHeader
        title="Metrics"
        subtitle={
          <>
            {`Run outcomes and cost ${rangeDescription(range)}.`}
            <div className="mt-0.5 text-xs text-ink-faint">
              {activeOrg ? `Workspace · ${activeOrg.name}` : 'Current workspace'}
            </div>
          </>
        }
        actions={
          <div className="flex flex-col items-end gap-2">
            <SegmentedTabs
              value={customOpen ? 'custom' : tab}
              onChange={onTabChange}
              options={WINDOW_OPTIONS}
              ariaLabel="Metrics window"
            />
            {(customOpen || tab === 'custom') && (
              <CustomRangeForm
                key={'from' in range ? `${range.from}:${range.to}` : 'new'}
                initial={'from' in range ? range : { from: utcDate(29), to: utcDate() }}
                onApply={onApplyCustom}
              />
            )}
          </div>
        }
      />

      <WorkspaceSwitcher subPath="metrics" />

      {query.isLoading ? (
        <LoadingState label="Loading metrics…" />
      ) : query.isError ? (
        <ErrorState
          message={errorMessage(query.error, 'Failed to load metrics')}
          onRetry={() => query.refetch()}
        />
      ) : query.data ? (
        <Content data={query.data} />
      ) : null}
    </div>
  );
}

function Content({ data }: { data: MetricsDto }) {
  const successPct =
    data.successRate == null ? '—' : `${Math.round(data.successRate * 100)}%`;
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
        <StatCard label="Total runs" value={String(data.totalRuns)} />
        <StatCard label="Success rate" value={successPct} />
        <StatCard label="Total cost" value={formatCost(data.totalCostUsd)} />
        <StatCard label="Avg cost / run" value={formatCost(data.avgCostUsd)} />
        <StatCard
          label="Avg duration"
          value={fmtDuration(data.avgDurationSeconds)}
        />
      </div>

      <StatusBreakdown byStatus={data.byStatus} />

      <div className="grid gap-6 lg:grid-cols-2">
        <RunsChart series={data.series} bucket={data.seriesBucket ?? 'day'} />
        <CostChart series={data.series} bucket={data.seriesBucket ?? 'day'} />
      </div>

      <RolesTable roles={data.roles} />
      <FleetBenchmarkSection data={data} />
      <TokenUsageSection tokenUsage={data.tokenUsage} />
      <LoopChurnSection
        avgLoopIterations={data.avgLoopIterations}
        totalGateFailures={data.totalGateFailures}
        gateFailureRate={data.gateFailureRate}
      />
      <ByProjectTable projects={data.byProject} />
      <ByLoopTable loops={data.byLoop} />
    </div>
  );
}
